import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';

export interface GlobalDetailPanelSkeletonProps {
  variant?: 'standard' | 'narrow' | 'auto';
  animation?: 'pulse' | 'wave' | 'shimmer' | 'none';
  className?: string;
}

export function GlobalDetailPanelSkeleton({
  variant = 'auto',
  animation = 'pulse',
  className,
}: GlobalDetailPanelSkeletonProps) {
  const isNarrow = variant === 'narrow';

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Header Section - avatar/icon, title, subtitle, badges */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start gap-3">
          <Skeleton animation={animation} className="w-10 h-10 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <Skeleton animation={animation} className="h-5 w-48 mb-1.5" />
            <Skeleton animation={animation} className="h-3.5 w-32" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton animation={animation} className="h-6 w-20 rounded-full" />
          </div>
        </div>
        {/* Contact info row */}
        <div className="flex items-center gap-2 mt-3 ml-[52px]">
          <Skeleton animation={animation} className="h-3 w-3 rounded-sm" />
          <Skeleton animation={animation} className="h-3 w-28" />
          {!isNarrow && (
            <>
              <Skeleton animation={animation} className="h-2 w-1 rounded-full" />
              <Skeleton animation={animation} className="h-3 w-36" />
            </>
          )}
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center gap-1 px-5 py-2 border-b border-border">
        <Skeleton animation={animation} className="h-7 w-12 rounded-lg" />
        <div className="w-px h-4 bg-border mx-1" />
        <Skeleton animation={animation} className="h-7 w-14 rounded-lg" />
        <Skeleton animation={animation} className="h-7 w-12 rounded-lg" />
        <div className="flex-1" />
        <Skeleton animation={animation} className="h-7 w-20 rounded-lg" />
      </div>

      {/* Tabs - hidden for narrow variant */}
      {!isNarrow && (
        <div className="border-b border-border px-5">
          <div className="flex items-center gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-1.5 py-2.5">
                <Skeleton animation={animation} className="h-3.5 w-3.5 rounded-sm" />
                <Skeleton animation={animation} className="h-3.5 w-16" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto px-6 py-5">
        <div className={cn('space-y-6', isNarrow ? 'max-w-3xl' : 'max-w-4xl')}>
          {/* Details Grid */}
          <div>
            <Skeleton animation={animation} className="h-5 w-32 mb-3" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg bg-muted/30 dark:bg-muted/10">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="space-y-1.5">
                  <Skeleton animation={animation} className="h-3 w-20 bg-muted/50" />
                  <Skeleton animation={animation} className="h-4 w-32" />
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <Skeleton animation={animation} className="h-5 w-24 mb-3" />
            <div className="space-y-2">
              <Skeleton animation={animation} className="h-4 w-full bg-muted/50" />
              <Skeleton animation={animation} className="h-4 w-full bg-muted/50" />
              <Skeleton animation={animation} className="h-4 w-3/4 bg-muted/50" />
            </div>
          </div>

          {/* Related Entities */}
          <div>
            <Skeleton animation={animation} className="h-5 w-36 mb-3" />
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-md border border-border/50"
                >
                  <Skeleton animation={animation} className="w-8 h-8 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <Skeleton animation={animation} className="h-4 w-40 mb-1" />
                    <Skeleton animation={animation} className="h-3 w-28 bg-muted/50" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Timestamps */}
          <div className="pt-4 border-t border-border/50">
            <div className="flex gap-4">
              <Skeleton animation={animation} className="h-3 w-36 bg-muted/50" />
              <Skeleton animation={animation} className="h-3 w-36 bg-muted/50" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
